import React, { useState, useRef, useCallback } from 'react';
import { useDrag } from '@use-gesture/react';
import { motion } from 'framer-motion';
import { ChevronRight, CheckCircle } from 'lucide-react';

import confetti from 'canvas-confetti';

interface CompleteWorkoutSliderProps {
  onComplete: () => void;
  disabled?: boolean;
}

const THRESHOLD = 0.85; // 85% of track = success
const THUMB_WIDTH = 56;

export const CompleteWorkoutSlider: React.FC<CompleteWorkoutSliderProps> = ({
  onComplete,
  disabled = false,
}) => {
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);

  const fireConfetti = useCallback(() => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (rect) {
      const originX = (rect.left + rect.width / 2) / window.innerWidth;
      const originY = rect.top / window.innerHeight;
      confetti({
        particleCount: 80,
        spread: 70,
        scalar: 1.4,
        origin: { x: originX, y: originY },
        colors: ['var(--action-primary-pressed)', 'var(--action-primary)', 'var(--ink)', '#6b8afd', '#fbbf24'],
        disableForReducedMotion: true,
      });
    }
    setTimeout(() => onComplete(), 600);
  }, [onComplete]);

  const getMaxDrag = useCallback(() => {
    const el = trackRef.current;
    if (!el) return 300;
    return Math.max(0, el.getBoundingClientRect().width - THUMB_WIDTH);
  }, []);

  const bind = useDrag(
    (state) => {
      if (disabled) return;
      const maxDrag = getMaxDrag();
      if (maxDrag <= 0) return;
      const rawProgress = state.movement[0] / maxDrag;
      const clamped = Math.max(0, Math.min(1, rawProgress));
      setProgress(clamped);

      if (state.last) {
        if (clamped >= THRESHOLD) {
          setCompleted(true);
          setProgress(1);
          fireConfetti();
        } else {
          setProgress(0);
        }
      }
    },
    {
      axis: 'x',
      bounds: () => {
        const el = trackRef.current;
        if (!el) return { left: 0, right: 300 };
        const w = el.getBoundingClientRect().width;
        return { left: 0, right: Math.max(0, w - THUMB_WIDTH) };
      },
      enabled: !disabled,
      pointer: { touch: true },
      from: () => [progress * getMaxDrag(), 0],
    }
  );

  const maxDrag = getMaxDrag();
  const thumbPositionPx = maxDrag > 0 ? progress * maxDrag : 0;

  // Fill: light grey to primary green (var(--action-primary) = 179, 231, 131)
  const PRIMARY_GREEN = 'var(--action-primary)';
  const fillR = Math.round(229 + (179 - 229) * progress);
  const fillG = Math.round(229 + (231 - 229) * progress);
  const fillB = Math.round(229 + (131 - 229) * progress);
  const fillColor = completed ? PRIMARY_GREEN : `rgb(${fillR}, ${fillG}, ${fillB})`;

  // Thumb: grey to green
  const thumbR = Math.round(163 + (179 - 163) * progress);
  const thumbG = Math.round(163 + (231 - 163) * progress);
  const thumbB = Math.round(163 + (131 - 163) * progress);
  const thumbColor = `rgb(${thumbR}, ${thumbG}, ${thumbB})`;

  // Once completed, render an identical static element to the final "Completed" bar
  // so there's no flash when WorkoutDetailsPage swaps the slider for the status div.
  if (completed) {
    return (
      <div
        ref={trackRef}
        className="flex-1 px-4 py-3 rounded-lg bg-primary-400 text-gray-800 dark:text-gray-900 flex items-center justify-center gap-2 font-medium"
      >
        <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-900" />
        <span>Completed</span>
      </div>
    );
  }

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-label="Slide to complete workout"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      className={`
        complete-workout-slider flex-1 relative h-12 rounded-lg overflow-hidden
        select-none touch-none cursor-grab active:cursor-grabbing
        ${disabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}
      `}
      style={{ touchAction: 'none' }}
      {...(disabled ? {} : bind())}
    >
      {/* Track background with "Complete Workout" label */}
      <div className="absolute inset-0 bg-gray-300/50 dark:bg-white/10 flex items-center justify-center z-0">
        <span className="text-theme-text dark:text-theme-off-white font-medium pointer-events-none">
          Complete Workout
        </span>
      </div>

      {/* Green fill -- clips the "Completed" text so it reveals as the user drags */}
      <div
        className="absolute left-0 top-0 bottom-0 rounded-lg z-[1] overflow-hidden"
        ref={fillRef}
        style={{
          width: thumbPositionPx + THUMB_WIDTH,
          backgroundColor: fillColor,
        }}
      >
        {/* Text centered relative to full track width, clipped by the fill */}
        <div
          className="h-full flex items-center justify-center pointer-events-none"
          style={{ width: trackRef.current?.getBoundingClientRect().width || '100vw' }}
        >
          <span className="text-gray-800 dark:text-gray-900 font-medium flex items-center gap-2 whitespace-nowrap">
            <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-900" />
            Completed
          </span>
        </div>
      </div>

      {/* Thumb */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-14 flex items-center justify-center rounded-lg shadow-md z-[2]"
        style={{
          x: thumbPositionPx,
          backgroundColor: thumbColor,
        }}
        transition={{
          type: 'tween',
          duration: 0.3,
          ease: 'easeOut',
        }}
      >
        <ChevronRight className="w-6 h-6 text-white" strokeWidth={2.5} />
      </motion.div>
    </div>
  );
};
